import { Injectable, inject } from '@angular/core';
import { celebrities, Celebrity, highlightText, Quiz } from 'funnel/src/app/data/quiz';
import type { Book } from 'shared/src/types/funnel-sample-content';
import { KindleFunnelFlowService } from '../../../../kindle-funnel/src/app/services/kindle-funnel-flow.service';
import { QuizStepKey } from '../../../../funnel/src/app/services/funnel-flow.service';
import { FunnelFlowConfig } from 'funnel/src/app/flow/flow-config';
import { romanceFunnelFlow } from '../romance-funnel.flow';
import {
  getRomanceBookIdsForSubgenres,
  RomanceSubgenreId,
  romanceFeaturedBookIds,
  romanceSubgenreIds,
} from '../data/romance-books';
import { RomanceResultMessageService } from './romance-result-message.service';

declare const $localize: any;

@Injectable({
  providedIn: 'root',
})
export class RomanceFunnelFlowService extends KindleFunnelFlowService {
  private readonly romanceResultMessageService = inject(RomanceResultMessageService);

  protected override getFlowConfig(): FunnelFlowConfig {
    return romanceFunnelFlow;
  }

  override customizeStepData(key: QuizStepKey): Quiz | undefined {
    if (key === 'wishReadAloud') {
      const optionTitles: Record<string, string> = {
        yes: $localize`:@@romanceWishYes:Yes, please`,
        no: $localize`:@@romanceWishNo:Not really`,
      };

      return {
        ...this.steps.wishReadAloud,
        title:
          $localize`:@@romanceWishTitlePrefix:Wish someone could read your ` +
          highlightText($localize`:@@romanceWishTitleHighlight:romance novels`) +
          $localize`:@@romanceWishTitleSuffix: to you?`,
        options: this.steps.wishReadAloud.options?.map(option => ({
          ...option,
          title: optionTitles[option.id] ?? option.title,
        })),
      };
    }

    if (key === 'romanceVoicePreference') {
      const optionTitles: Record<string, string> = {
        friendlyFemale: $localize`:@@romanceVoiceFriendlyFemale:A friendly female voice`,
        calmMale: $localize`:@@romanceVoiceCalmMale:A calm male voice`,
        balancedNeutral: $localize`:@@romanceVoiceBalancedNeutral:Balanced and neutral`,
        expressiveStoryteller: $localize`:@@romanceVoiceExpressive:An expressive storyteller`,
        exploreOptions: $localize`:@@romanceVoiceExplore:Let me explore`,
      };

      return {
        ...this.steps.romanceVoicePreference,
        title:
          $localize`:@@romanceVoicePrefTitlePrefixV2:What voice do you want ` +
          highlightText($localize`:@@romanceVoicePrefTitleHighlightV2:in your ear`) +
          $localize`:@@romanceVoicePrefTitleSuffixV2:?`,
        subtitle: $localize`:@@romanceVoicePrefSubtitleV2:You can change this any time.`,
        options: this.steps.romanceVoicePreference.options?.map(option => ({
          ...option,
          title: optionTitles[option.id] ?? option.title,
        })),
      };
    }

    if (key === 'amountInfo') {
      const data = super.customizeStepData(key) || this.steps.amountInfo;
      const techCrunchQuote = $localize`:@@amountInfoTechCrunchQuote:Peech is a text-to-speech app that actually sounds good`;
      const mediaCiteSecondPart = $localize`:@@romanceAmountInfoCiteSuffixV2:- so your book keeps going even when your day doesn't slow down.`;

      return {
        ...data,
        title:
          $localize`:@@romanceAmountInfoTitlePrefix:Join ` +
          highlightText(
            $localize`:@@romanceAmountInfoTitleHighlightV2:1,000,000+ romance listeners`
          ),
        mediaCite: `${techCrunchQuote} ${mediaCiteSecondPart}`,
      };
    }

    if (key === 'audioExperienceInfo') {
      const data = super.customizeStepData(key) || this.steps.audioExperienceInfo;
      const bookSourceAnswer = this.getWebQuizAnswers()['romanceBookSource'];
      const bookSourceIds = Array.isArray(bookSourceAnswer) ? bookSourceAnswer : [];
      const ownsKindleBooks = bookSourceIds.includes('kindle');

      return {
        ...data,
        title:
          $localize`:@@romanceAudioInfoTitlePrefixV2:Your ` +
          highlightText($localize`:@@romanceAudioInfoTitleHighlightV2:Kindle and ebooks`) +
          $localize`:@@romanceAudioInfoTitleSuffixV2:, read aloud`,
        subtitle: ownsKindleBooks
          ? $localize`:@@romanceAudioInfoSubtitleKindle:The very book on your Kindle, no separate audiobook to buy. Press play while dinner's on the stove.`
          : $localize`:@@romanceAudioInfoSubtitleOther:Any ebook, PDF, or library loan, played back like an audiobook. Press play while dinner's on the stove.`,
      };
    }

    if (key === 'romanceHonestInfo') {
      const frictionAnswer = this.getWebQuizAnswers()['kindleFriction'];
      const frictionIds: unknown[] = Array.isArray(frictionAnswer)
        ? frictionAnswer
        : frictionAnswer
          ? [frictionAnswer]
          : [];
      // The first of these that was ticked drives the copy, regardless of
      // the order they were ticked in.
      const frictionPriority = [
        'fallAsleep',
        'noQuietTime',
        'longChapters',
        'wantHandsFree',
        'voicesMatter',
      ] as const;
      const topFriction = frictionPriority.find(id => frictionIds.includes(id));

      if (topFriction === 'fallAsleep') {
        return {
          ...this.steps.romanceHonestInfo,
          title:
            $localize`:@@romanceHonestFallAsleepTitlePrefix:Falling asleep mid-chapter ` +
            highlightText($localize`:@@romanceHonestFallAsleepTitleHighlight:isn't failure`),
          subtitle: $localize`:@@romanceHonestSubtitleSleepV2:You're not failing the book, you're just tired. Let the story keep going while you drift off.`,
        };
      }

      const subtitleByFriction: Partial<Record<(typeof frictionPriority)[number], string>> = {
        noQuietTime: $localize`:@@romanceHonestSubtitleNoQuietTime:Quiet time is rare. Listening doesn't need it - the story plays over dishes, traffic and noise.`,
        longChapters: $localize`:@@romanceHonestSubtitleLongChapters:Long chapters shrink when someone reads them to you. You just press play.`,
        wantHandsFree: $localize`:@@romanceHonestSubtitleHandsFree:Hands busy, ears free. That's exactly where listening fits.`,
        voicesMatter: $localize`:@@romanceHonestSubtitleVoicesMatter:You're right, the voice makes or breaks it. Ours sound like a narrator, not a robot.`,
      };
      const subtitle = topFriction
        ? subtitleByFriction[topFriction]
        : $localize`:@@romanceHonestSubtitleDefaultV2:Life gets loud. Listening keeps the story going anyway.`;

      return {
        ...this.steps.romanceHonestInfo,
        title:
          $localize`:@@romanceHonestDefaultTitlePrefix:Not a ` +
          highlightText($localize`:@@romanceHonestDefaultTitleHighlight:discipline problem`),
        subtitle,
      };
    }

    if (key === 'personTypeInfo') {
      const data = super.customizeStepData(key) || this.steps.personTypeInfo;
      // The parent picks the rive file by personality type (extrovert /
      // introvert / ambivert for "both"), so key the copy off it.
      const copyByType: Partial<Record<string, { title: string; subtitle: string }>> = {
        extrovert: {
          title:
            $localize`:@@romancePersonTypeExtrovertTitlePrefix:A voice that feels ` +
            highlightText($localize`:@@romancePersonTypeExtrovertTitleHighlight:like company`),
          subtitle: $localize`:@@romancePersonTypeExtrovertV2:Expect lively, expressive narrators - the kind that make a slow burn feel like a friend spilling the tea.`,
        },
        introvert: {
          title:
            $localize`:@@romancePersonTypeIntrovertTitlePrefix:A voice that pulls you ` +
            highlightText($localize`:@@romancePersonTypeIntrovertTitleHighlight:all the way in`),
          subtitle: $localize`:@@romancePersonTypeIntrovertV2:Expect calm, immersive narrators, so it's just you and the morally grey love interest.`,
        },
        ambivert: {
          title:
            $localize`:@@romancePersonTypeAmbivertTitlePrefix:Voices that ` +
            highlightText($localize`:@@romancePersonTypeAmbivertTitleHighlight:match your mood`),
          subtitle: $localize`:@@romancePersonTypeAmbivertV2:Lively when you want company, calm when you want to disappear into the chapter.`,
        },
      };
      const copy = data.rive?.fileName ? copyByType[data.rive.fileName] : undefined;

      return copy ? { ...data, title: copy.title, subtitle: copy.subtitle } : data;
    }

    if (key === 'kindleFriction') {
      return {
        ...this.steps.kindleFriction,
        title:
          $localize`:@@romanceFrictionTitlePrefixV2:What keeps your ` +
          highlightText($localize`:@@romanceFrictionTitleHighlightV2:books`) +
          $localize`:@@romanceFrictionTitleSuffixV2: unfinished?`,
        subtitle: $localize`:@@romanceFrictionSubtitleV2:The DNF pile has its reasons.`,
        options: [
          { id: 'noQuietTime', title: $localize`:@@romanceFrictionNoQuietTimeV2:No quiet time` },
          { id: 'fallAsleep', title: $localize`:@@romanceFrictionFallAsleepV2:I fall asleep` },
          {
            id: 'longChapters',
            title: $localize`:@@romanceFrictionLongChaptersV2:Chapters feel too long`,
          },
          {
            id: 'wantHandsFree',
            title: $localize`:@@romanceFrictionHandsFreeV2:I want hands-free`,
          },
          {
            id: 'voicesMatter',
            title: $localize`:@@romanceFrictionVoicesMatterV2:The voice matters`,
          },
        ],
      };
    }

    if (key === 'visionCareInfo') {
      return {
        ...this.steps.visionCareInfo,
        title:
          $localize`:@@romanceVisionCareInfoTitlePrefix:Give your eyes the ` +
          highlightText($localize`:@@romanceVisionCareInfoTitleHighlight:evening off`),
        subtitle: $localize`:@@romanceVisionCareInfoSubtitleV2:Small print at night is tiring on anyone. Glasses off, book on - the chapter comes to you.`,
        rive: this.steps.visionCareInfo.rive && {
          ...this.steps.visionCareInfo.rive,
          fileName: 'girl_headphones',
        },
      };
    }

    if (key === 'time') {
      return {
        ...this.steps.time,
        title:
          $localize`:@@romanceTimeTitlePrefixV2:When do you want a ` +
          highlightText($localize`:@@romanceTimeTitleHighlightV2:book playing`) +
          $localize`:@@romanceTimeTitleSuffixV2:?`,
        subtitle: undefined,
        options: [
          {
            id: 'inMorning',
            title: $localize`:@@romanceTimeInMorningV2:In the morning`,
            emoji: '☕️',
          },
          {
            id: 'workingOut',
            title: $localize`:@@romanceTimeWorkingOutV2:While working out`,
            emoji: '🏃‍♂️',
          },
          {
            id: 'onCommute',
            title: $localize`:@@romanceTimeOnCommuteV2:On my commute`,
            emoji: '🚗',
          },
          {
            id: 'takingBreak',
            title: $localize`:@@romanceTimeTakingBreakV2:On a break`,
            emoji: '🛋',
          },
          { id: 'chores', title: $localize`:@@romanceTimeChoresV2:Doing chores`, emoji: '🧹' },
          {
            id: 'workingOrStudying',
            title: $localize`:@@romanceTimeWorkStudyV2:While working or studying`,
            emoji: '💻',
          },
        ],
      };
    }

    if (key === 'audioCustomizationInfo') {
      return {
        ...this.steps.audioCustomizationInfo,
        title: highlightText($localize`:@@almostDone:We are almost done!`),
        subtitle: $localize`:@@romanceAudioCustomizationInfoSubtitleV2:We already know a bit about you - now let's turn your answers into voices and books you'll love.`,
      };
    }

    if (key === 'listeningTime') {
      return {
        ...this.steps.listeningTime,
        title:
          $localize`:@@romanceListeningTimeTitlePrefixV2:How much listening ` +
          highlightText($localize`:@@romanceListeningTimeTitleHighlightV2:fits your day`) +
          $localize`:@@romanceListeningTimeTitleSuffixV2:?`,
        subtitle: $localize`:@@romanceListeningTimeSubtitleV2:Be realistic - we build around this.`,
        options: [
          { id: 'minutes0515', title: $localize`:@@romanceListeningTime0515:5 to 15 minutes` },
          { id: 'minutes1530', title: $localize`:@@romanceListeningTime1530:15 to 30 minutes` },
          { id: 'minutes3060', title: $localize`:@@romanceListeningTime3060:30 to 60 minutes` },
          { id: 'hours123', title: $localize`:@@romanceListeningTime123:1 to 2 hours` },
          { id: 'hours2plus', title: $localize`:@@romanceListeningTime2plus:2+ hours` },
        ],
      };
    }

    if (key === 'book1' || key === 'book2' || key === 'book3' || key === 'yourBook') {
      const data = super.customizeStepData(key) || this.steps[key];

      return {
        ...data,
        title:
          $localize`:@@romanceBookListenTitlePrefix:Would this ` +
          highlightText($localize`:@@romanceBookListenTitleHighlight:feel good`) +
          $localize`:@@romanceBookListenTitleSuffix: as audio?`,
      };
    }

    if (key === 'prediction') {
      const data = super.customizeStepData(key) || this.steps[key];

      return {
        ...data,
        title:
          $localize`:@@romancePredictionTitlePrefixV2:Story time is about to be your ` +
          highlightText($localize`:@@romancePredictionTitleHighlightV2:favorite part`),
        subtitle: $localize`:@@romancePredictionSubtitleV2:A red light, a sink full of dishes, a slow Sunday - all of it turns into the next chapter, picking up right where you left off.`,
      };
    }

    return super.customizeStepData(key);
  }

  protected override getResultStep(): Quiz {
    const { messages, metricProperties } = this.romanceResultMessageService.generateMessages({
      answers: this.getWebQuizAnswers(),
      subgenreIds: this.getRomanceSubgenreIds(),
    });

    this.trackResultMetricOnce('web_kindle_result_metric_shown', metricProperties);

    return {
      ...this.steps.result,
      title:
        $localize`:@@romanceResultTitlePrefixV2:Your ` +
        highlightText($localize`:@@romanceResultTitleHighlightV3:listening plan`) +
        $localize`:@@romanceResultTitleSuffixV2: is ready`,
      messages,
    };
  }

  // Romance-coded voices for the romance audience: Morgan Freeman (control,
  // perennial most-loved-voice poll winner) and Brad Pitt (rom-com icon),
  // promoted into the base lineup Aug 2026; Zendaya joins as the female
  // voice this funnel tests.
  protected override getCelebLineup(): Celebrity[] {
    return [celebrities.morganFreeman, celebrities.bradPitt, celebrities.zendaya];
  }

  getRomanceSubgenreIds(): RomanceSubgenreId[] {
    const answer = this.getWebQuizAnswers()['romanceSubgenres'];
    const ids = Array.isArray(answer) ? answer : [];

    return ids.filter((id): id is RomanceSubgenreId =>
      romanceSubgenreIds.includes(id as RomanceSubgenreId)
    );
  }

  // Full replacement of the kindle implementation (no super call): the
  // kindle pool interleaves non-fiction interest books, appends
  // motivation-mapped books, and ends with defaultBookIds - all non-fiction
  // titles. This funnel samples only the romance subgenre pool: subgenre
  // picks first (one title per picked subgenre per round), then the
  // featured titles as the tail. The pool always contains the 8 featured
  // titles, so assignBooks never reaches its (non-fiction) fallback books.
  protected override getRelevantBooks(): Book[] {
    const bookIds = getRomanceBookIdsForSubgenres(this.getRomanceSubgenreIds());

    romanceFeaturedBookIds.forEach(bookId => {
      if (!bookIds.includes(bookId)) {
        bookIds.push(bookId);
      }
    });

    return this.sampleContentService.getBooks(bookIds);
  }
}
